import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { StatusTimeline } from "@/components/shared/Records";
import { useAuth } from "@/context/AuthContext";
import { api, download, errMsg } from "@/lib/api";
import { RUN_STATUS } from "./Payroll";
import { PayrollGrid } from "./PayrollGrid";
import { LineDialog } from "./PayrollLine";

const ACTIONS = { borrador: [["revisada", "Marcar revisada", "rrhh.planilla.generar"], ["anulada", "Anular", "rrhh.planilla.generar"]],
  revisada: [["aprobada", "Aprobar", "rrhh.planilla.aprobar"], ["borrador", "Devolver a borrador", "rrhh.planilla.generar"]],
  aprobada: [["cerrada", "Cerrar período", "rrhh.planilla.cerrar"]] };

export default function PayrollDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const [run, setRun] = useState(null);
  const [line, setLine] = useState(null);
  const [act, setAct] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => api.get(`/rrhh/payroll/runs/${id}`).then((r) => setRun(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!run) return <div className="text-[#5b6656]">Cargando…</div>;
  const draft = run.status === "borrador";
  const doTransition = async () => {
    try { const { data } = await api.post(`/rrhh/payroll/runs/${id}/transition`, { to: act, note }); setRun(data); setAct(null); setNote(""); toast.success(`Pre-nómina ${RUN_STATUS[data.status][1].toLowerCase()}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const recalc = async () => { try { const { data } = await api.post(`/rrhh/payroll/runs/${id}/recalc`); setRun(data); toast.success("Recalculada desde la asistencia"); } catch (e) { toast.error(errMsg(e)); } };
  const canEdit = draft && can("rrhh.planilla.conceptos_editar");
  return (
    <div data-testid="payroll-detail-page">
      <Link to="/rrhh/pre-nomina" className="inline-flex items-center gap-1 text-sm font-semibold text-[#258F00] mb-4"><ArrowLeft size={15} />Pre-nómina</Link>
      <PageHeader eyebrow={<span className="flex items-center gap-2 font-mono">{run.code} <StatusBadge testid="run-status-badge" status={RUN_STATUS[run.status][0]} label={RUN_STATUS[run.status][1]} /></span>} title={`Período ${run.period}`} subtitle={`Cálculo interno de referencia · parámetros: ${run.params?.ref || "—"}. Haga clic en un trabajador para ver su detalle. Celdas: código de asistencia y +horas extra del día (amarillo = sin marca de salida).`}
        actions={<>
          {draft && can("rrhh.planilla.generar") && <Button data-testid="run-recalc-btn" variant="outline" className="rounded-full" onClick={recalc}><RefreshCw size={15} className="mr-1" />Recalcular</Button>}
          <Button data-testid="run-export-btn" variant="outline" className="rounded-full" onClick={() => download(`/rrhh/payroll/runs/${id}/export`, {}, `prenomina_${run.period}.xlsx`)}><Download size={15} className="mr-1" />Excel</Button>
          {(ACTIONS[run.status] || []).filter(([, , p]) => can(p)).map(([to, l]) => <Button key={to} data-testid={`run-to-${to}-btn`} variant={["anulada", "borrador"].includes(to) ? "outline" : "default"} className="rounded-full" onClick={() => setAct(to)}>{l}</Button>)}
        </>} />
      <div className="syc-card overflow-hidden mb-5"><PayrollGrid run={run} onOpen={setLine} /></div>
      <StatusTimeline items={run.history} labels={Object.fromEntries(Object.entries(RUN_STATUS).map(([k, v]) => [k, v[1]]))} />
      <LineDialog run={run} line={line} canEdit={canEdit} onClose={() => setLine(null)} onSaved={(d) => { setRun(d); setLine(null); }} />
      <Dialog open={!!act} onOpenChange={(o) => !o && setAct(null)}>
        <DialogContent data-testid="run-transition-dialog">
          <DialogHeader><DialogTitle>Cambiar a “{act && RUN_STATUS[act][1]}”</DialogTitle></DialogHeader>
          <Field label={["borrador", "anulada"].includes(act) ? "Motivo *" : "Observación"}><Textarea data-testid="run-note-input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          <DialogFooter><Button data-testid="run-transition-confirm-btn" className="rounded-full" onClick={doTransition}>Confirmar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
